import React, { useState } from 'react';
import { MessageSquare, X } from 'lucide-react';

/**
 * StudentScholarDialogue — floating tlk.io launcher for MOBILE users.
 *
 * On desktop (md+) the inline chat panel in UnifiedFederationHub is the
 * primary surface — the floating launcher is hidden there via `md:hidden`
 * to avoid a duplicate chat UI and to free up the desktop corner.
 *
 * The floating widget uses tlk.io's URL-based iframe embed so it does
 * NOT collide with the canonical `#tlkio` element that lives in the hub.
 *
 * Layout contract:
 *   • position: fixed, bottom-right (74px x 18px)
 *   • collapsed default (56px circular launcher)
 *   • expanded ≤ 350×450 desktop / 90vw×60vh mobile
 *   • z-index: 60 (above ticker z-50, under modals ≥ 1000)
 *   • never displaces flow; never blocks primary content
 */
export default function StudentScholarDialogue() {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid="student-scholar-dialogue"
      aria-label="Student & Scholar Dialogue — live chat (mobile)"
      className="fixed z-[60] pointer-events-none md:hidden"
      style={{ bottom: '74px', right: '18px' }}
    >
      {open ? (
        <div
          data-testid="student-scholar-dialogue-container"
          className="pointer-events-auto rounded-2xl border-2 border-purple-500/40 bg-[#0b0a14] shadow-2xl shadow-purple-500/30 overflow-hidden flex flex-col"
          style={{
            width: 'min(350px, 90vw)',
            height: 'min(450px, 60vh)',
            zIndex: 60,
          }}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-purple-500/30 bg-black/60 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="relative inline-flex items-center justify-center w-2.5 h-2.5 flex-shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span
                className="text-[10px] font-mono uppercase tracking-[0.18em] text-purple-200 truncate"
                data-testid="dialogue-header-title"
              >
                #nextxus-federation
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="dialogue-close"
              aria-label="Close chat"
              className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full text-purple-200 hover:bg-white/10 transition-colors flex-shrink-0"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <iframe
            title="Federation Chat — nextxus-federation"
            src="https://tlk.io/nextxus-federation"
            data-testid="tlkio-widget"
            style={{
              width: '100%',
              flex: '1 1 auto',
              border: 0,
              backgroundColor: '#0b0a14',
            }}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="dialogue-launcher"
          aria-label="Open Federation chat"
          title="Federation Chat · #nextxus-federation"
          className="pointer-events-auto inline-flex items-center justify-center rounded-full bg-purple-600 hover:bg-purple-500 border-2 border-purple-300/60 shadow-xl shadow-purple-500/40 transition-transform hover:scale-105"
          style={{ width: '56px', height: '56px' }}
        >
          <MessageSquare className="w-6 h-6 text-white" />
          <span className="sr-only">Open chat</span>
        </button>
      )}
    </div>
  );
}
